import { useState, type FormEvent } from "react";
import SectionHeading from "./SectionHeading";

const SUBMIT_URL = "https://readdy.ai/api/form/daf899htto0ceqqnh7k0";

const contactChannels = [
  { icon: "ri-mail-send-line", title: "投稿与合作", value: "editor@chigua-cinema.com", type: "mailto", note: "影评投稿 / 内容合作" },
  { icon: "ri-feedback-line", title: "意见与反馈", value: "hello@chigua-cinema.com", type: "mailto", note: "使用反馈 / 问题求助" },
  { icon: "ri-time-line", title: "响应时间", value: "工作日 10:00 - 19:00", type: "text", note: "通常 1 - 2 个工作日内回复" },
];

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "loading" | "success">("idle");
  const [error, setError] = useState("");

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    setStatus("loading");
    const params = new URLSearchParams();
    fd.forEach((v, k) => {
      if (k === "website_alt") return;
      params.append(k, String(v));
    });

    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const text = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      const msg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text || "";
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("idle");
        setError(msg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("idle");
      setError("网络异常，请稍后再试。");
    }
  };

  return (
    <section id="contact" className="section-pad scroll-mt-20 bg-background-100">
      <div className="container-x">
        <SectionHeading
          eyebrow="联系我们"
          title="想求片、投稿或反馈？来聊聊"
          desc="无论是想让我们推荐某类电影、分享你的影评，还是指出片库中的错误，都欢迎告诉我们。"
          align="center"
        />

        <div className="mx-auto mt-10 grid max-w-5xl gap-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
          {/* 联系方式卡片 */}
          <div className="flex flex-col gap-4">
            {contactChannels.map((c) => (
              <div key={c.title} className="flex items-center gap-4 rounded-2xl border border-background-200 bg-background-50 p-5">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                  <i className={c.icon + " text-xl"} aria-hidden="true"></i>
                </span>
                <div className="min-w-0">
                  <h3 className="font-heading text-base font-bold text-foreground-950">{c.title}</h3>
                  <p className="truncate text-sm text-primary-700">
                    {c.type === "mailto" ? <a href={c.type + ":" + c.value} className="hover:underline">{c.value}</a> : <span>{c.value}</span>}
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-500">{c.note}</p>
                </div>
              </div>
            ))}
            <div className="rounded-2xl border border-dashed border-background-300 bg-background-50 p-5">
              <p className="text-sm font-semibold text-foreground-800">关注我们</p>
              <div className="mt-3 flex items-center gap-3">
                {[
                  { icon: "ri-wechat-line", label: "微信" },
                  { icon: "ri-weibo-line", label: "微博" },
                  { icon: "ri-douban-line", label: "豆瓣" },
                  { icon: "ri-bilibili-line", label: "B站" },
                ].map((s) => (
                  <span key={s.label} className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-secondary-800 transition-colors hover:bg-primary-100 hover:text-primary-700">
                    <i className={s.icon + " text-lg"} aria-hidden="true" title={s.label}></i>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* 表单 */}
          <form
            data-readdy-form
            onSubmit={onSubmit}
            className="relative rounded-2xl border border-background-200 bg-background-50 p-6 md:p-7"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">怎么称呼你</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  maxLength={30}
                  placeholder="你的昵称"
                  className="w-full rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">联系邮箱</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  maxLength={60}
                  placeholder="用于回复你的邮箱"
                  className="w-full rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">留言类型</label>
              <select
                id="topic"
                name="topic"
                required
                className="w-full appearance-none rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              >
                <option value="">请选择留言类型</option>
                <option value="求片推荐">求片推荐</option>
                <option value="使用反馈">使用反馈</option>
                <option value="影评投稿">影评投稿</option>
                <option value="内容合作">内容合作</option>
                <option value="其他">其他</option>
              </select>
            </div>

            <div className="mt-4">
              <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">留言内容</label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="想看的电影、想反馈的问题，或你的投稿内容……"
                className="w-full resize-none rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              ></textarea>
              <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
            </div>

            {/* 蜜罐字段 */}
            <input
              type="text"
              name="website_alt"
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              readOnly
              className="field-mote"
            />

            {error ? (
              <p role="alert" className="mt-4 flex items-start gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                <i className="ri-error-warning-line mt-0.5" aria-hidden="true"></i>
                {error}
              </p>
            ) : null}
            {status === "success" ? (
              <p role="status" className="mt-4 flex items-start gap-2 rounded-xl bg-accent-100 px-4 py-3 text-sm text-accent-900">
                <i className="ri-checkbox-circle-line mt-0.5" aria-hidden="true"></i>
                已收到你的留言，编辑部会尽快回复你，感谢支持！
              </p>
            ) : null}

            <button
              type="submit"
              disabled={status === "loading"}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary-600 px-6 py-3 text-sm font-semibold whitespace-nowrap text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {status === "loading" ? (
                <>
                  <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                  提交中…
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill" aria-hidden="true"></i>
                  提交留言
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}